import { FC, useRef } from 'react';
import {
  CardHeader, Card, Box, Skeleton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Chart, { useChart } from '@mgm/common/components/charts';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { AnimatedDivider } from '../../../../components';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getIsOrdersLoading } from '../../../../store/orders/selectors';
import { getCurrencyByCode } from '../../../../store/common/selectors';
import { TSingleTeam } from '../../../../types';

type TTeamSalesCommissionBarChart = TSingleTeam;

const TeamSalesCommissionBarChart: FC<TTeamSalesCommissionBarChart> = ({ team }) => {
  const { t } = useTranslation(['commissions', 'common']);
  const isOrdersLoading = useAppSelector(getIsOrdersLoading);
  const getCurrency = useAppSelector(getCurrencyByCode);
  const chartRef = useRef<any>(null);

  const seriesData = [
    {
      name: t('netSales'),
      data: [team.netSales],
    },
    {
      name: t('commission'),
      data: [team.commission],
    },
  ];

  const chartOptions = useChart({
    chart: {
      stacked: false,
      events: {
        mounted: (chart) => {
          chartRef.current = chart;
        },
      },
    },
    colors: [
      '#D0AEFF',
      '#A5F3FF',
      '#A4F7CC',
      '#FFF7AE',
      '#FFBD98',
      '#826AF9',
      '#2D99FF',
      '#2CD9C5',
      '#FFE700',
      '#FF6C40',
    ],
    stroke: {
      width: 0,
    },
    xaxis: {
      categories: [team.name],
    },
    yaxis: {
      labels: {
        formatter: (value: number) => `${value.toFixed(2)} ${getCurrency().mark}`,
      },
    },
    tooltip: {
      y: {
        formatter: (value: number) => `${value.toFixed(2)} ${getCurrency().mark}`,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        columnWidth: '40%',
      },
    },
  });

  return (
    <Card>
      <CardHeader sx={{ p: 2 }} title="Sales Vs Commission" />
      <AnimatedDivider loading={isOrdersLoading} />
      <Box sx={{ mx: 3 }} dir="ltr">
        {/* eslint-disable-next-line no-nested-ternary */}
        {isOrdersLoading ? (
          <Box pt={3}>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i?.toString()} variant="rectangular" height={40} sx={{ mb: 2 }} />
            ))}
          </Box>
        ) : seriesData[0].data.some((val) => val > 0) ? (
          <Chart type="bar" series={seriesData} options={chartOptions} height={400} />
        ) : (
          <Box height={400}>
            <CustomNoRowsOverlay text={t('noDataForToday', { ns: 'common' })} />
          </Box>
        )}
      </Box>
    </Card>
  );
};

export { TeamSalesCommissionBarChart };
